import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import './table.scss';

import { TableContainer, Card, CardContent, Table as MuiTable, TableHead, TableRow, TableBody, TableFooter, TablePagination, TableCell } from '@mui/material';

import { Checkbox } from '@dxlm/components';

import TableHeaderColumn, { ITableColumn } from './table-header-column';
import TableDataRow, { IOrderChangeEvent } from './table-data-row';
import TableLoader from './table-loader';
import NoDataRow from './no-data-row';

import { TSortDirection } from '@dxlm/interfaces';


interface ITableProps {
    columns: ITableColumn[];
    rows: any[];
    idDataKey?: string;
    filters?: {[key: string]: any};
    filtersChanged?: (filters: {[key: string]: any}) => void;
    sortKey?: string;
    sortDirection?: TSortDirection;
    sortingChanged?: (dataKey: any, direction: TSortDirection) => void;
    pageSize?: number;
    pageIndex?: number;
    totalRows?: number;
    pagingChanged?: (pageSize: number, pageIndex: number) => void;
    isLoading?: boolean;
    rowClicked?: (dataRow: any) => void;
    rowLink?: (dataRow: any) => string;
    noCard?: boolean;
    noPagination?: boolean;
    disabled?: boolean;

    orderable?: boolean;
    orderChanged?: (events: IOrderChangeEvent[]) => void;

    checkboxSelection?: boolean;
    selectedRowIds?: any[];
    selectedRowIdsChanged?: (rowIds: any[]) => void;
}
const Table: FunctionComponent<ITableProps> = (props: ITableProps) => {

    const [checkboxState, setCheckboxState] = useState<'none' | 'checked' | 'indeterminate'>('none');
    const [tableRows, setTableRows] = useState<any[]>([]);
    const tableBodyRef = useRef<HTMLTableSectionElement>();

    const handleFilterChanged = (dataKey: string, newFilterValue: any) => {
        if (props.disabled) {
            return;
        }

        const filtersClone = Object.assign({}, props.filters ?? {});
        filtersClone[dataKey] = newFilterValue;
        
        if (props.filtersChanged) {
            props.filtersChanged(filtersClone);
        }
    };

    const calculateSorting = (clickedDataKey: string) => {
        if (props.disabled || !props.sortingChanged || typeof props.sortingChanged !== 'function') {
            return;
        }

        const newSortDirection: TSortDirection = props.sortKey === clickedDataKey
            ? props.sortDirection === 'ASC' ? 'DESC' : 'ASC'
            : props.sortDirection;

        props.sortingChanged(clickedDataKey, newSortDirection);
    };

    const handlePageSizeChanged = (pageSize: number) => {
        if (props.pagingChanged && !props.disabled) {
            props.pagingChanged(pageSize, 0);
        }
    };
    const handlePageIndexChanged = (pageIndex: number) => {
        if (props.pagingChanged && !props.disabled) {
            props.pagingChanged(props.pageSize, pageIndex);
        }
    }

    const getIdDataKey = () => props.idDataKey ?? 'id';

    const getVisibleIds = (): any[] => {
        const idDataKey = getIdDataKey();
        return (props.rows ?? []).map(x => x[idDataKey]);
    }

    const handleHeaderCheckboxChanged = () => {
        const visibleIds = getVisibleIds();
        const selectedRowIdsClone = Object.assign([], props.selectedRowIds ?? []);
        if (checkboxState !== 'none') {
            visibleIds.forEach(x => {
                const index = selectedRowIdsClone.indexOf(x);
                if (index >= 0) {
                    selectedRowIdsClone.splice(index, 1);
                }
            });
        } else {
            selectedRowIdsClone.push(...visibleIds);
        }

        if (props.selectedRowIdsChanged) {
            props.selectedRowIdsChanged(selectedRowIdsClone);
        }
    };
    const handleRowCheckboxSelectionChanged = (row: any) => {
        const id = row[getIdDataKey()];
        const selectedRowIdsClone = Object.assign([], props.selectedRowIds ?? []);
        const existingIndex = selectedRowIdsClone.indexOf(id);
        if (existingIndex >= 0) {
            selectedRowIdsClone.splice(existingIndex, 1);
        } else {
            selectedRowIdsClone.push(id);
        }

        if (props.selectedRowIdsChanged) {
            props.selectedRowIdsChanged(selectedRowIdsClone);
        }
    };

    const handleRowsOrdered = (events: IOrderChangeEvent[]) => {
        const indexedRows: Array<{ row: any, originalIndex: number, newIndex: number }> = tableRows.map((x, index) => ({ row: x, originalIndex: index, newIndex: index }));

        for (const event of events) {
            const matchingRow = indexedRows.find(x => x.originalIndex === event.oldIndex);
            if (matchingRow) {
                matchingRow.newIndex = event.newIndex;
            }
        }

        indexedRows.sort((a, b) => a.newIndex - b.newIndex);
        setTableRows(indexedRows.map(x => x.row));

        if (props.orderChanged && typeof props.orderChanged === 'function') {
            props.orderChanged(events);
        }
    };

    const getVisibleColumns = () => props.columns.filter(x => x.visible !== false);

    useEffect(() => {
        const visibleIds = getVisibleIds();
        const selectedIdsCount = visibleIds.filter(x => (props.selectedRowIds ?? []).indexOf(x) >= 0).length;
        setCheckboxState(selectedIdsCount === 0 ? 'none' : selectedIdsCount === visibleIds.length ? 'checked' : 'indeterminate');
    }, [props.selectedRowIds, tableRows]);

    useEffect(() => {
        setTableRows(props.rows ?? []);
    }, [props.rows]);

    const mainTable = (
        <TableContainer component={props.noCard ? null : CardContent} style={{padding: props.noCard ? '0px' : '15px 30px 30px 30px'}}>
            <MuiTable>
                <TableHead>
                    <TableRow>
                        {
                            props.orderable && (
                                <TableCell sx={{width: '42px'}}>
                                    Order
                                </TableCell>
                            )
                        }
                        {
                            props.checkboxSelection && (
                                <TableCell sx={{width: '42px'}}>
                                    <Checkbox
                                        value={checkboxState === 'checked'}
                                        indeterminate={checkboxState === 'indeterminate'}
                                        onChange={handleHeaderCheckboxChanged}
                                        disabled={props.disabled}
                                    />
                                </TableCell>
                            )
                        }
                        {
                            getVisibleColumns().map(c => (
                                <TableHeaderColumn
                                    columnDef={c}
                                    filterValue={!props.filters || !props.filters[String(c.dataKey) ?? c.title] ? '' : props.filters[String(c.dataKey) ?? c.title]}
                                    filterChanged={(newVal) => handleFilterChanged(String(c.dataKey), newVal)}
                                    sortKey={props.sortKey}
                                    sortDirection={props.sortDirection}
                                    sortingClicked={() => calculateSorting(String(c.dataKey))}
                                    key={String(c.dataKey) ?? c.title}
                                    disabled={props.isLoading || props.disabled}
                                />
                            ))
                        }
                    </TableRow>
                </TableHead>
                {
                    props.isLoading ? (
                        <TableLoader columnCount={getVisibleColumns().length} />
                    ) : tableRows?.length > 0 ? (
                        <TableBody ref={tableBodyRef}>
                            {
                                tableRows.map((row, index) => (
                                    <TableDataRow
                                        columns={getVisibleColumns()}
                                        row={row}
                                        rowIndex={index}
                                        onSelected={() => props.rowClicked ? props.rowClicked(row) : null}
                                        disabled={props.disabled}
                                        key={row[getIdDataKey()]}
                                        checkboxSelection={props.checkboxSelection}
                                        checkboxChecked={props.selectedRowIds?.indexOf(row[getIdDataKey()]) >= 0}
                                        checkboxSelectionChanged={() => handleRowCheckboxSelectionChanged(row)}
                                        rowLink={props.rowLink}
                                        orderable={props.orderable}
                                        orderChanged={handleRowsOrdered}
                                        tableBodyRef={tableBodyRef}
                                    />
                                ))
                            }
                        </TableBody>
                    ) : (
                        <NoDataRow columnCount={getVisibleColumns().length} />
                    )
                }
                {
                    !props.isLoading && props.totalRows > 0 && props.noPagination !== true && !props.orderable && (
                        <TableFooter>
                            <TableRow>
                                {
                                    props.checkboxSelection && (
                                        <TableCell colSpan={3}>
                                            {props.selectedRowIds?.length} row{props.selectedRowIds?.length === 1 ? '' : 's'} selected
                                        </TableCell>
                                    )
                                }
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    colSpan={getVisibleColumns().length - (props.checkboxSelection ? 2 : 0)}
                                    count={props.totalRows}
                                    rowsPerPage={props.pageSize ?? 10}
                                    page={props.pageIndex ?? 0}
                                    onPageChange={(e, newPageIndex) => handlePageIndexChanged(newPageIndex)}
                                    onRowsPerPageChange={(e) => handlePageSizeChanged(parseInt(e.target.value, 10))}
                                />
                            </TableRow>
                        </TableFooter>
                    )
                }
            </MuiTable>
        </TableContainer>
    )

    if (props.noCard) {
        return mainTable;
    }

    return (
        <Card>
            {mainTable}
        </Card>
    )
};

export default Table;